///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, Input, OnInit } from '@angular/core';
import {
    EngineAntennaDTO,
    EngineProcessTypeEnum,
    EngineProductDTO,
    ExtendedProductdata,
    PdbChannelFrequenciesEnum,
    ProductTypeEnum,
    SynchronizationModeEnum
} from '../../../../../generated/shared-types.model';
import { ProjectDialogProductsService } from '../../project-dialog-products.service';
import { GtthConfigLevel } from '../../../../../shared/product-radio-config/config-level.enum';
import { convertFeetToMeter, convertMeterToFeet } from '../../../../../shared/utils';
import { GtthAlertService } from '../../../../../services/alert.service';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { GtthLogicalTopology } from '../../../../../project-view-panel/logical-topology/entities/logical-topology';
import { GtthProductNameLinkDirective } from '../../../../../shared/directives/style-directives/product-name-link.directive';
import { ProjectUnits } from '../../../../project-enums';

@Component({
    selector: 'winde-select-products',
    templateUrl: 'select-products.component.html',
    styleUrls: ['select-products.component.scss'],
    standalone: false
})
export class SelectProductsComponent implements OnInit {
    @Input() viewInReadOnly: boolean;

    @Input() defaultBuProductPerLayer: number[];
    @Input() defaultTuProductPerLayer: number[];
    @Input() selectedProductsPerLayer: ExtendedProductdata[][];
    @Input() productUnits: string;
    @Input() engineType: EngineProcessTypeEnum;
    @Input() customerId: number;
    @Input() frozenPairs: Map<number, number[]>;

    public activeLayerIndex: number;
    public radioConfigIsValid = [];
    public configLevel = GtthConfigLevel;
    public isShowRadioConfig = false;
    public usedProduct: EngineProductDTO[];
    public usedProductTu: Record<number, EngineProductDTO[]>;
    public selectedEngineRadio: EngineProductDTO;
    public selectedPairToEngineRadio: EngineProductDTO;
    public editForm: FormGroup;
    public productsToUseArr: FormArray;
    public noneProductConfig: FormGroup;
    public hasMhProduct: boolean;
    public hasMhTgGpsBuProduct: boolean;
    public hasMhTgDpllBuProduct: boolean;
    public needHideNoneProduct = false;
    public thresholdMaxValue: number;
    public availableAntennas: EngineAntennaDTO[][];

    private defaultBuProductPerLayerControl: AbstractControl;
    private defaultTuProductPerLayerControl: AbstractControl;
    private readonly thresholdMaxValueMeters = 500000;
    private readonly thresholdMaxValueFeet = convertMeterToFeet(this.thresholdMaxValueMeters);

    constructor(
        public readonly dialogProductsService: ProjectDialogProductsService,
        private readonly alertService: GtthAlertService,
        private readonly formBuilder: FormBuilder
    ) {}

    @Input() set layerIndex(index: number) {
        this.activeLayerIndex = index;
        this.refreshForm();
    }

    ngOnInit(): void {
        this.usedProduct = [];
        this.usedProductTu = {};
        this.availableAntennas = this.dialogProductsService.getAvailableAnennas();

        const mhTgTuProducts = [];

        this.dialogProductsService.engineRadioList
            .filter((product: EngineProductDTO) => product.productType === ProductTypeEnum.TG_MH && !product.isPtP)
            .forEach((product: EngineProductDTO) => {
                if (product.isTU) {
                    mhTgTuProducts.push(product);
                }
            });

        this.dialogProductsService.engineRadioList
            .filter((product: EngineProductDTO) => !product.isTU)
            .forEach((product: EngineProductDTO) => {
                this.usedProduct.push(product);
                if (product.isPtP) {
                    this.usedProductTu[product.id] = [product];
                } else if (product.productType === ProductTypeEnum.TG_MH) {
                    this.usedProductTu[product.id] = [...mhTgTuProducts];
                }
            });

        this.thresholdMaxValue = this.productUnits === ProjectUnits.FEET ? this.thresholdMaxValueFeet : this.thresholdMaxValueMeters;

        this.initForm();
    }

    checkFrequenciesValidation(areFrequenciesValid: boolean) {
        this.radioConfigIsValid[this.selectedEngineRadio.id] = areFrequenciesValid;
    }

    // modificationData - is array that have next data for handling any frequency changes in pairs:
    // first element - main engine id,
    // second element - deleted items from main device array
    // third element - added items to main device array
    isModifiedRadioList(modificationData: [number, number[], number[]]) {
        const currentEngineRadioId: number = modificationData[0];
        const deletedItemsFromMainDevice: number[] = modificationData[1];
        const addedItemsToMainDevice: number[] = modificationData[2];

        // allDeviceIndexPairs - is an array that has all device index pairs (E.g. [ [12, 12], [12, 14], [14, 15] ])
        // on each pair we have the next: first index - is the main device index, second index - is the related device index
        const allDeviceIndexPairs: [number, number][] = this.productsToUseArr.controls.map((control) => [
            +control.get('usedProd').value,
            +control.get('usedProdTU').value
        ]);

        // First change wave: we always handle pairs where the current engine radio is the main, not the associated one,
        // For example, if the main device has index 12, should handle next pairs [ [12, 12], [12, 14] ]
        const firstChangeWaveIndexPairs: [number, number][] = allDeviceIndexPairs.filter(
            (currentPair) => currentPair[0] === currentEngineRadioId
        );

        //start first changing wave
        this.modifyFrequenciesOnAssociatedPair(firstChangeWaveIndexPairs, deletedItemsFromMainDevice, addedItemsToMainDevice);

        // After first change wave we have to get all changed associated indexes (E.g. [ 12, 14])
        const changedAssociatedIndexesAfterFirstChange: number[] = firstChangeWaveIndexPairs.map((currentIndexPair) => currentIndexPair[1]);

        // We should get pairs from allDeviceIndexPairs where changed associated indexes have the main indexes role
        // (E.g. [ [12, 12], [12, 14], [14, 15] ])
        const secondChangeWaveIndexPairs: [number, number][] = allDeviceIndexPairs.filter((pair) =>
            changedAssociatedIndexesAfterFirstChange.includes(pair[0])
        );

        // Second change wave: (this allows implementing Transitivity)
        this.modifyFrequenciesOnAssociatedPair(secondChangeWaveIndexPairs, deletedItemsFromMainDevice, addedItemsToMainDevice);
    }

    refreshForm() {
        if (!this.editForm) {
            return;
        }

        this.hasMhProduct = false;
        this.isShowRadioConfig = false;

        this.productsToUseArr.clear();

        this.selectedProductsPerLayer[this.activeLayerIndex].forEach(() => this.productsToUseArr.push(this.initBlankFormGroup()));

        this.productsToUseArr.controls.forEach((usedProductsConfig, i) => {
            const product: ExtendedProductdata = this.selectedProductsPerLayer[this.activeLayerIndex][i];

            const productThreshold = this.productUnits === ProjectUnits.FEET ? convertMeterToFeet(product.threshold) : product.threshold;

            usedProductsConfig.get('threshold').setValue(productThreshold, { emitEvent: false });
            usedProductsConfig.get('usedAnt0').setValue(product.usedAnt0, { emitEvent: false });
            usedProductsConfig.get('usedAnt1').setValue(product.usedAnt1, { emitEvent: false });
            usedProductsConfig.get('usedProd').setValue(product.usedProd === -1 ? null : product.usedProd, { emitEvent: false });
            usedProductsConfig.get('usedProdTU').setValue(product.usedProdTU === -1 ? null : product.usedProdTU, { emitEvent: false });
        });

        this.validateProducts();

        if (this.hasMhProduct) {
            if (this.defaultBuProductPerLayer[this.activeLayerIndex]) {
                this.defaultBuProductPerLayerControl.setValue(this.defaultBuProductPerLayer[this.activeLayerIndex], {
                    emitEvent: false
                });
            }
            if (this.defaultTuProductPerLayer[this.activeLayerIndex]) {
                this.defaultTuProductPerLayerControl.setValue(this.defaultTuProductPerLayer[this.activeLayerIndex], {
                    emitEvent: false
                });
            }
        }
    }

    hasToShowTUProduct(productId: number): boolean {
        if (this.engineType === EngineProcessTypeEnum.RINGDES_V_1211) {
            return false;
        }
        const product: EngineProductDTO = this.dialogProductsService.getEngineProduct(productId);
        return product && product.productType === ProductTypeEnum.TG_MH && !product.isPtP;
    }

    getProductName(productId: number): string {
        return this.dialogProductsService.getEngineProduct(productId).name;
    }

    getDefaultBuLink(): string {
        const usedBuId = this.defaultBuProductPerLayerControl.value;
        const defaultBUName = this.dialogProductsService.availableBu.find((bu) => bu.key === usedBuId).value;
        return GtthProductNameLinkDirective.getMHProductLink(defaultBUName);
    }

    getDefaultTuLink(): string {
        const usedTuId = this.defaultTuProductPerLayerControl.value;
        const defaultTUName = this.dialogProductsService.availableTu.find((bu) => bu.key === usedTuId).value;
        return GtthProductNameLinkDirective.getMHProductLink(defaultTUName);
    }

    editProductConfig(productId: number, rowIndex: number) {
        if (this.radioConfigIsValid.indexOf(false) !== -1) {
            this.alertService.showAlert('error.validation.field.invalidPdbConfig');
            return false;
        }

        this.selectedEngineRadio = this.dialogProductsService.engineRadioList.find((config) => {
            return config.id === productId;
        });

        const pairEngineRadioId = this.productsToUseArr.controls[rowIndex].value.usedProdTU;
        this.selectedPairToEngineRadio = this.usedProductTu[productId]?.find((elem) => elem.id === pairEngineRadioId);

        this.isShowRadioConfig = false;
        setTimeout(() => (this.isShowRadioConfig = true), 0);
    }

    validateAndApply(): boolean {
        if (this.editForm.invalid) {
            let errorCode;
            for (const productsRow of this.productsToUseArr.controls) {
                if (productsRow.get('threshold').invalid) {
                    errorCode = Object.keys(productsRow.get('threshold').errors)[0];
                    break;
                } else if (productsRow.get('usedProd').invalid) {
                    errorCode = Object.keys(productsRow.get('usedProd').errors)[0];
                    break;
                }
            }

            if (errorCode) {
                this.alertService.showAlert('error.validation.field.' + errorCode);
            }

            return false;
        }

        if (this.radioConfigIsValid.indexOf(false) !== -1) {
            this.alertService.showAlert('error.validation.field.invalidPdbConfig');
            return false;
        }

        this.selectedProductsPerLayer[this.activeLayerIndex] = this.productsToUseArr.controls
            .map((item: FormGroup) => item.getRawValue())
            .map((product: ExtendedProductdata) => {
                if (this.productUnits === ProjectUnits.FEET) {
                    product.threshold = convertFeetToMeter(product.threshold);
                }
                return product;
            });

        if (this.hasMhProduct) {
            this.defaultBuProductPerLayer[this.activeLayerIndex] = this.defaultBuProductPerLayerControl.value;
            this.defaultTuProductPerLayer[this.activeLayerIndex] = this.defaultTuProductPerLayerControl.value;
        }

        return true;
    }

    onAddUsedProduct(): void {
        const newProduct = this.initBlankFormGroup();
        if (this.productsToUseArr.length) {
            const lastExistingProduct = this.productsToUseArr.at(this.productsToUseArr.length - 1);
            newProduct.get('threshold').setValue(lastExistingProduct.get('threshold').value + 1);
        }

        this.productsToUseArr.push(this.setUsedProductsConfigValues(newProduct, this.noneProductConfig.get('usedProd').value));
        this.noneProductConfig.reset({ threshold: 0, usedProd: null });

        this.validateProducts();
    }

    onInsertUsedProduct(row: AbstractControl<any>): void {
        const rowIndex = this.findRowIndex(this.productsToUseArr, row);
        const newProduct = this.initBlankFormGroup();

        newProduct.get('threshold').setValue(row.get('threshold').value);
        newProduct.get('usedAnt0').setValue(row.get('usedAnt0').value);
        newProduct.get('usedAnt1').setValue(row.get('usedAnt1').value);
        newProduct.get('usedProd').setValue(row.get('usedProd').value);
        newProduct.get('usedProdTU').setValue(row.get('usedProdTU').value);

        this.productsToUseArr.insert(rowIndex + 1, newProduct);

        this.validateProducts();
    }

    onChangeUsedProduct(index: number): void {
        const product = this.productsToUseArr.at(index) as FormGroup;
        const productId = product.get('usedProd').value;
        if (productId === null) {
            let i = this.productsToUseArr.length;
            while (i >= index) {
                this.productsToUseArr.removeAt(i, { emitEvent: false });
                i--;
            }
        } else {
            this.setUsedProductsConfigValues(product, productId);
        }
        this.validateProducts();
    }

    onRemoveUsedProduct(row: AbstractControl<any>): void {
        const index = this.findRowIndex(this.productsToUseArr, row);
        this.productsToUseArr.removeAt(index, { emitEvent: false });
        this.validateProducts();
    }

    needHideNoneProductRow(index: number): void {
        const product = this.productsToUseArr.at(index) as FormGroup;
        const lastIndex = this.productsToUseArr.length - 1;

        if (index !== lastIndex) {
            return;
        }

        this.needHideNoneProduct = product.get('threshold').value === 0;
    }

    onChangeTuProduct(index: number): void {
        const product = this.productsToUseArr.at(index) as FormGroup;
        const tuProduct = product.get('usedProdTU').value;
        const tuAntenna = this.availableAntennas[tuProduct][0].id;
        product.get('usedAnt1').setValue(tuAntenna, { emitEvent: false });
    }

    public validateProducts(): void {
        this.hasMhProduct = this.productsToUseArr.controls.some(
            (selectedProduct: FormGroup) =>
                this.dialogProductsService.getEngineProduct(selectedProduct.get('usedProd').value)?.productType === ProductTypeEnum.MH
        );
        this.hasMhTgGpsBuProduct = this.productsToUseArr.controls.some((selectedProduct: FormGroup) => {
            const product = this.dialogProductsService.getEngineProduct(selectedProduct.get('usedProd').value);
            return product
                ? product.productType === ProductTypeEnum.TG_MH &&
                      product.isBU &&
                      product.pdbConfig.synchronizationMode === SynchronizationModeEnum.GPS
                : false;
        });
        this.hasMhTgDpllBuProduct = this.productsToUseArr.controls.some((selectedProduct: FormGroup) => {
            const product = this.dialogProductsService.getEngineProduct(selectedProduct.get('usedProd').value);
            return product
                ? product.productType === ProductTypeEnum.TG_MH &&
                      product.isBU &&
                      product.pdbConfig.synchronizationMode === SynchronizationModeEnum.DPLL
                : false;
        });

        this.productsToUseArr.controls.forEach((item: FormGroup, index) => {
            const thresholdField = item.get('threshold');
            const antennaField = item.get('usedAnt0');
            const antenna1Field = item.get('usedAnt1');
            const productField = item.get('usedProd');
            const productTUField = item.get('usedProdTU');

            thresholdField.setErrors(null, { emitEvent: false });
            antennaField.setErrors(null, { emitEvent: false });
            antenna1Field.setErrors(null, { emitEvent: false });
            productField.setErrors(null, { emitEvent: false });
            productTUField.setErrors(null, { emitEvent: false });

            if (this.viewInReadOnly) {
                thresholdField.disable({ emitEvent: false });
                antennaField.disable({ emitEvent: false });
                antenna1Field.disable({ emitEvent: false });
                productField.disable({ emitEvent: false });
                productTUField.disable({ emitEvent: false });
                return;
            }

            if (productField.value === null) {
                return;
            }

            const product = this.dialogProductsService.getEngineProduct(productField.value);

            const mhFamilyCollision =
                (product.productType === ProductTypeEnum.MH && (this.hasMhTgGpsBuProduct || this.hasMhTgDpllBuProduct)) ||
                (product.productType === ProductTypeEnum.TG_MH && product.isBU && this.hasMhProduct);

            if (mhFamilyCollision) {
                productField.setErrors({ productsTypeError: true }, { emitEvent: false });
            }

            const syncModeCollision =
                product.productType === ProductTypeEnum.TG_MH &&
                product.isBU &&
                ((product.pdbConfig.synchronizationMode === SynchronizationModeEnum.GPS && this.hasMhTgDpllBuProduct) ||
                    (product.pdbConfig.synchronizationMode === SynchronizationModeEnum.DPLL && this.hasMhTgGpsBuProduct));

            if (syncModeCollision) {
                productField.setErrors({ productsSyncTypeError: true }, { emitEvent: false });
            }

            this.checkBuPtpCollisions(index);

            thresholdField.enable({ emitEvent: false });

            const minValue = index > 0 ? this.productsToUseArr.controls[index - 1].get('threshold').value : 0;
            const isLast = index === this.productsToUseArr.controls.length - 1;

            const validators = [
                Validators.required,
                (control: AbstractControl): ValidationErrors | null =>
                    isLast && thresholdField.value === 0 ? Validators.required(control) : Validators.min(minValue)(control),
                Validators.max(this.thresholdMaxValue)
            ];

            thresholdField.setValidators(validators);

            thresholdField.updateValueAndValidity({ emitEvent: false });

            antennaField.enable({ emitEvent: false });
            if (
                antennaField.disabled ||
                antennaField.value == null ||
                !this.availableAntennas[productField.value].find((antenna) => antenna.id === antennaField.value)
            ) {
                antennaField.setValue(null, { emitEvent: false });
                antennaField.setValidators([Validators.required]);
            }

            if (!this.availableAntennas[productTUField.value]?.find((antenna) => antenna.id === antenna1Field.value)) {
                antenna1Field.setValue(null, { emitEvent: false });
                if (productTUField.value != null) {
                    antenna1Field.setValidators([Validators.required]);
                }
            }
        });
    }

    initForm() {
        this.productsToUseArr = this.formBuilder.array([]);

        this.defaultBuProductPerLayerControl = this.formBuilder.control({
            value:
                this.defaultBuProductPerLayer[this.activeLayerIndex] ||
                this.defaultBuProductPerLayer[GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX],
            disabled: this.viewInReadOnly
        });

        this.defaultTuProductPerLayerControl = this.formBuilder.control({
            value:
                this.defaultTuProductPerLayer[this.activeLayerIndex] ||
                this.defaultTuProductPerLayer[GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX],
            disabled: this.viewInReadOnly
        });

        this.noneProductConfig = this.formBuilder.group({
            threshold: this.formBuilder.control({ value: 0, disabled: true }),
            usedProd: this.formBuilder.control(null)
        });

        this.editForm = this.formBuilder.group({
            productsToUse: this.productsToUseArr,
            noneProductConfig: this.noneProductConfig,
            defaultMHProducts: this.formBuilder.group({
                defaultBuProductPerLayer: this.defaultBuProductPerLayerControl,
                defaultTuProductPerLayer: this.defaultTuProductPerLayerControl
            })
        });

        this.refreshForm();
    }

    pairMayCorruptFrozenLinks(productsRow: AbstractControl<any>): boolean {
        if (productsRow.dirty) {
            return false;
        }

        const productA = productsRow.get('usedProd').value;
        const productB = productsRow.get('usedProdTU').value;

        return this.frozenPairs.has(productA) && this.frozenPairs.get(productA).includes(productB);
    }

    private setUsedProductsConfigValues(usedProducts: FormGroup, productId: number): FormGroup {
        const antennaId = this.availableAntennas[productId][0].id;
        const isTgMh = this.dialogProductsService.getEngineProduct(productId).productType === ProductTypeEnum.TG_MH;
        let tuProductId = null;
        let tuAntenna = null;

        if (isTgMh) {
            tuProductId = this.usedProductTu[productId][0].id;
            tuAntenna = this.usedProductTu[productId][0].pdbConfig.antennas[0].id;
        }

        usedProducts.get('usedProd').setValue(productId, { emitEvent: false });
        usedProducts.get('usedAnt0').setValue(antennaId, { emitEvent: false });
        usedProducts.get('usedProdTU').setValue(tuProductId, { emitEvent: false });
        usedProducts.get('usedAnt1').setValue(tuAntenna, { emitEvent: false });
        return usedProducts;
    }

    private initBlankFormGroup(): FormGroup {
        return this.formBuilder.group({
            threshold: this.formBuilder.control(0),
            usedAnt0: this.formBuilder.control(null),
            usedAnt1: this.formBuilder.control(null),
            usedProd: this.formBuilder.control(null),
            usedProdTU: this.formBuilder.control(null)
        });
    }

    // this method allows you to change the frequencies on the coupled pair based on the main pair
    private modifyFrequenciesOnAssociatedPair(
        indexPairs: [number, number][],
        deletedItemsFromMainDevice: number[],
        addedItemsFromMainDevice: number[]
    ) {
        indexPairs.forEach(([mainDeviceIndex, associatedIndex]: [number, number]) => {
            // in case when a device doesn't have an associated pair,
            // we skip modifying frequencies on an associated pair
            if (!associatedIndex) {
                return;
            }

            const foundedPairRadio: EngineProductDTO = this.usedProductTu[mainDeviceIndex].find((elem) => elem.id === associatedIndex);

            // Remove frequencies in an associated pair based on the main pair
            foundedPairRadio.pdbConfig.channelConfigs[0].frequencies = foundedPairRadio.pdbConfig.channelConfigs[0].frequencies.filter(
                (frequency: PdbChannelFrequenciesEnum) => !deletedItemsFromMainDevice.includes(frequency)
            );

            // Add frequencies in an associated pair based on the main pair
            foundedPairRadio.pdbConfig.channelConfigs[0].frequencies.push(...addedItemsFromMainDevice);
            foundedPairRadio.pdbConfig.channelConfigs[0].frequencies = [
                ...new Set(foundedPairRadio.pdbConfig.channelConfigs[0].frequencies)
            ];
        });
    }

    private checkBuPtpCollisions(index: number) {
        if (index === 0 || index === this.productsToUseArr.length - 1) {
            return;
        }

        const currentProductField = this.productsToUseArr.controls[index].get('usedProd');
        const currentProduct = this.dialogProductsService.getEngineProduct(currentProductField.value);

        const previousProductField = this.productsToUseArr.controls[index - 1].get('usedProd');
        const previousProduct = this.dialogProductsService.getEngineProduct(previousProductField.value);

        if (currentProduct.isBU && previousProduct.isPtP) {
            currentProductField.setErrors({ invalidProductSequence: true }, { emitEvent: false });
            previousProductField.setErrors({ invalidProductSequence: true }, { emitEvent: false });
        }
    }

    private findRowIndex(productsToUseArr: FormArray, row: AbstractControl<any>) {
        for (let i = 0; i < productsToUseArr.length; i++) {
            if (productsToUseArr.at(i) === row) {
                return i;
            }
        }
    }
}
